import { isAuthBypassActive } from './envDetection';
import {
  getStoredAccessToken,
  readSupabaseTokensFromLocalStorage,
  safeLocalStorageGetItem,
  setStoredAccessToken,
  stripSupabaseRefreshTokenFromStorage,
  syncSupabaseAccessTokenInStorage,
} from './supabaseAuth';
import { AUTH_TIMEOUT_MS, TimeoutError, withTimeout } from './withTimeout';

// Vite statically replaces the exact token `import.meta.env.VITE_API_URL` at
// build time. Do NOT add optional chaining before `.env` — it defeats the
// replacement and the value silently becomes undefined at runtime.
// try/catch covers Playwright/E2E where import.meta.env doesn't exist.
let API_BASE = '';
try {
  API_BASE = (import.meta.env.VITE_API_URL as string) || '';
} catch {
  API_BASE = '';
}

function resolveUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (API_BASE) {
    return `${API_BASE.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
  }
  return url; // no base configured → relative (local dev / same-origin)
}

export class ApiClient {
  // Prevent thundering herd refreshes if multiple requests hit 401 simultaneously.
  private refreshInFlight: Promise<boolean> | null = null;
  private mfaRedirectInFlight = false;

  // ── MFA FIX ──────────────────────────────────────────────────────────
  // When true, suppresses ALL automatic 401 retry + 403 MFA redirect logic.
  //
  // During MFA flows (enrollment on /setup-mfa, verification on /auth/mfa,
  // inline MFA on /login), the Supabase SDK holds an aal1 session in memory.
  // If tryRefreshSession() fires (from a background API 401/403), it
  // overwrites localStorage with a NEW aal1 token, corrupting the SDK's
  // in-memory state and causing mfa.challenge()/verify()/enroll() to fail
  // with bad_jwt / session_missing / no user found.
  //
  // Set BEFORE any MFA SDK calls. Clear AFTER MFA completes or user
  // navigates away from MFA.
  // ─────────────────────────────────────────────────────────────────────
  // Initialize from URL path at construction time to close the race window
  // where TenantContext/AuthProvider fire API calls before React useEffect
  // sets the flag. On a full page load of /auth/mfa or /setup-mfa, the flag
  // is already true before any fetch() runs.
  private _mfaFlowActive = (() => {
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      return path === '/auth/mfa' || path === '/setup-mfa';
    } catch {
      return false;
    }
  })();

  public setMfaFlowActive(active: boolean): void {
    this._mfaFlowActive = active;
  }

  public get mfaFlowActive(): boolean {
    return this._mfaFlowActive;
  }
  // ── END MFA FIX ──────────────────────────────────────────────────────

  public markSetupMfaInFlight(): void {
    this.mfaRedirectInFlight = true;
  }

  private mergeHeaders(primary: HeadersInit, secondary?: HeadersInit): HeadersInit {
    if (!secondary) {
      return primary;
    }
    const merged = new Headers(primary as HeadersInit);
    const extra = new Headers(secondary as HeadersInit);
    extra.forEach((value, key) => merged.set(key, value));
    return merged;
  }

  private getFallbackHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-tenant-slug': (import.meta?.env?.VITE_TENANT_SLUG as string) || '',
      'x-user-id': safeLocalStorageGetItem('afl_user_id') || '',
    };
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.location !== 'undefined';
  }

  private maybeRedirectToMfaChallenge(reason: string): void {
    if (!this.isBrowser()) return;
    if (this.mfaRedirectInFlight) return;

    const path = window.location.pathname || '';
    // Avoid loops on auth routes.
    if (
      path === '/login' ||
      path.startsWith('/auth/') ||
      path === '/setup-mfa' ||
      path === '/auth' ||
      path === '/auth/callback'
    ) {
      return;
    }

    this.mfaRedirectInFlight = true;

    const redirect = `${window.location.pathname || '/'}${window.location.search || ''}${
      window.location.hash || ''
    }`;

    let storedOk = false;
    try {
      window.sessionStorage?.setItem('PW_MFA_REDIRECT', redirect);
      storedOk = true;
    } catch {
      // ignore
    }

    console.warn(`[ApiClient] Redirecting to /auth/mfa (${reason})`);
    try {
      const mfaUrl = storedOk ? '/auth/mfa' : `/auth/mfa?redirect=${encodeURIComponent(redirect)}`;
      window.location.assign(mfaUrl);
    } catch {
      // ignore
    }
  }

  private async isMfaRequiredResponse(response: Response): Promise<boolean> {
    if (response.status !== 403) return false;
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return false;
    try {
      const payload = await response.clone().json();
      return payload?.code === 'MFA_REQUIRED';
    } catch {
      return false;
    }
  }

  private maybeRedirectToLogin(reason: string): void {
    if (!this.isBrowser()) return;

    if (isAuthBypassActive()) {
      console.warn(`[ApiClient] Skipping login redirect in bypass mode (${reason})`);
      return;
    }

    const path = window.location.pathname || '';
    if (
      path === '/login' ||
      path.startsWith('/auth/') ||
      path === '/setup-mfa' ||
      path === '/auth' ||
      path === '/auth/callback'
    ) {
      return;
    }

    console.warn(`[ApiClient] Redirecting to /login (${reason})`);
    try {
      window.location.assign('/login');
    } catch {
      // ignore
    }
  }

  public async tryRefreshSession(): Promise<boolean> {
    // ── MFA FIX: Never refresh during MFA flows ──
    if (this._mfaFlowActive) {
      return false;
    }

    // Deduplicate in-flight refreshes
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const refreshResponse = await withTimeout(
          this.postWithoutAuth('/api/auth/refresh', undefined, {
            credentials: 'include',
          }),
          AUTH_TIMEOUT_MS,
          '/api/auth/refresh',
        );
        if (!refreshResponse.ok) {
          console.warn(
            '[ApiClient] Failed to refresh via auth cookie endpoint after 401:',
            refreshResponse.status,
            refreshResponse.statusText,
          );
          return false;
        }

        const payload = await refreshResponse.json().catch(() => null);
        const accessToken =
          typeof payload?.access_token === 'string' && payload.access_token.length > 0
            ? payload.access_token
            : null;

        if (!accessToken) {
          console.warn('[ApiClient] Refresh response missing access token');
          return false;
        }

        setStoredAccessToken(accessToken);
        syncSupabaseAccessTokenInStorage(
          accessToken,
          (import.meta?.env?.VITE_SUPABASE_URL as string) || '',
        );
        stripSupabaseRefreshTokenFromStorage((import.meta?.env?.VITE_SUPABASE_URL as string) || '');
        return true;
      } catch (e) {
        if (e instanceof TimeoutError) {
          console.warn('[ApiClient] /api/auth/refresh timed out after 401');
          return false;
        }
        console.warn('[ApiClient] Error refreshing via /api/auth/refresh after 401:', e);
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const accessToken =
      getStoredAccessToken() ||
      readSupabaseTokensFromLocalStorage((import.meta?.env?.VITE_SUPABASE_URL as string) || '')
        .accessToken;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const tenantSlug =
      safeLocalStorageGetItem('selected_tenant_slug') ||
      (import.meta?.env?.VITE_TENANT_SLUG as string) ||
      '';
    headers['x-tenant-slug'] = tenantSlug;

    const userId = safeLocalStorageGetItem('afl_user_id');
    if (userId) headers['x-user-id'] = userId;

    return headers;
  }

  private async request(
    url: string,
    init:
      | (RequestInit & {
          __retried?: boolean;
          __skipAuth?: boolean;
          __skip401Retry?: boolean;
        })
      | undefined = {},
  ): Promise<Response> {
    const skipAuth = Boolean((init as any)?.__skipAuth);
    const skip401Retry = Boolean((init as any)?.__skip401Retry);

    const { __retried, __skipAuth, __skip401Retry, ...fetchInit } = (init ?? {}) as RequestInit & {
      __retried?: boolean;
      __skipAuth?: boolean;
      __skip401Retry?: boolean;
    };
    void __skipAuth;
    void __skip401Retry;

    let mergedHeaders: HeadersInit;
    try {
      const headers = skipAuth
        ? ({
            'Content-Type': 'application/json',
          } as HeadersInit)
        : await this.getAuthHeaders();
      mergedHeaders = this.mergeHeaders(headers, init?.headers);
    } catch (error) {
      console.warn('[ApiClient] Failed to build auth headers:', error);

      const baseHeaders = skipAuth
        ? ({ 'Content-Type': 'application/json' } as HeadersInit)
        : this.getFallbackHeaders();
      mergedHeaders = this.mergeHeaders(baseHeaders, fetchInit?.headers);
    }

    const response = await fetch(resolveUrl(url), {
      cache: fetchInit?.cache ?? 'no-store',
      ...fetchInit,
      headers: mergedHeaders,
    });

    // ── MFA FIX: Skip ALL retry/redirect logic during MFA flows ──
    // This prevents tryRefreshSession() from corrupting the Supabase SDK's
    // in-memory session while mfa.challenge()/verify()/enroll() are active.
    if (this._mfaFlowActive) {
      return response;
    }

    // If unauthorized (401), attempt a single refresh + retry
    if (response.status === 401 && !__retried && !skip401Retry) {
      const authHeader = new Headers(mergedHeaders).get('authorization');
      if (!authHeader) return response;

      const refreshed = await this.tryRefreshSession();
      if (!refreshed) {
        this.maybeRedirectToLogin(`401 and refresh failed`);
        return response;
      }

      const retryHeaders = await this.getAuthHeaders();
      const mergedRetryHeaders = this.mergeHeaders(retryHeaders, fetchInit?.headers);
      const retryResponse = await fetch(resolveUrl(url), {
        cache: fetchInit?.cache ?? 'no-store',
        ...fetchInit,
        headers: mergedRetryHeaders,
      });

      if (retryResponse.status === 403 && (await this.isMfaRequiredResponse(retryResponse))) {
        this.maybeRedirectToMfaChallenge('403 MFA_REQUIRED after 401 retry');
      }

      return retryResponse;
    }

    // If MFA is required (403), redirect to MFA step-up flow
    if (response.status === 403 && !__retried) {
      const authHeader = new Headers(mergedHeaders).get('authorization');
      if (authHeader && (await this.isMfaRequiredResponse(response))) {
        const refreshed = await this.tryRefreshSession();
        this.maybeRedirectToMfaChallenge(
          refreshed ? '403 MFA_REQUIRED' : '403 MFA_REQUIRED (refresh unavailable)',
        );
      }
    }

    return response;
  }

  clearSessionCache(): void {
    // no-op
  }

  async get(url: string, init?: RequestInit): Promise<Response> {
    return this.request(url, { ...(init ?? {}), method: init?.method ?? 'GET' });
  }

  async post(url: string, data?: any, init?: RequestInit): Promise<Response> {
    return this.request(url, {
      ...(init ?? {}),
      method: 'POST',
      body: (init as RequestInit | undefined)?.body ?? (data ? JSON.stringify(data) : undefined),
    });
  }

  async postWithoutAuth(url: string, data?: any, init?: RequestInit): Promise<Response> {
    return this.request(url, {
      ...(init ?? {}),
      method: 'POST',
      body: (init as RequestInit | undefined)?.body ?? (data ? JSON.stringify(data) : undefined),
      __skipAuth: true,
      __skip401Retry: true,
    });
  }

  async put(url: string, data?: any, init?: RequestInit): Promise<Response> {
    return this.request(url, {
      ...(init ?? {}),
      method: 'PUT',
      body: (init as RequestInit | undefined)?.body ?? (data ? JSON.stringify(data) : undefined),
    });
  }

  async patch(url: string, data?: any, init?: RequestInit): Promise<Response> {
    return this.request(url, {
      ...(init ?? {}),
      method: 'PATCH',
      body: (init as RequestInit | undefined)?.body ?? (data ? JSON.stringify(data) : undefined),
    });
  }

  async delete(url: string, init?: RequestInit): Promise<Response> {
    return this.request(url, { ...(init ?? {}), method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
